import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Bookmark, BookmarkCheck, Check, FileQuestion, Heart, Layers, Pencil, Target,
} from 'lucide-react';
import { apiErrorMessage, articleService, examService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Breadcrumbs, Button, EmptyState, GlobalShell, Loader, PagerNav,
} from '../components/ui';
import { ArticleBody } from '../components/articles/ArticleBody';
import { Comments } from '../components/articles/Comments';
import {
  ActionButton, EngagementLine, LinksCard, ReaderHeader, SharePopover, TocCard, useReadingProgress,
} from '../components/articles/Reader';
import { headingsOf } from '../utils/articles';
import '../components/articles/Reader.css';

/** Leitura completa (logado): curtir, salvar, compartilhar, sumário com progresso, vínculos e comentários. */
function ArticleView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [saved, setSaved] = useState(false);
  const [read, setRead] = useState(false);
  const [shares, setShares] = useState(0);
  const [comments, setComments] = useState(0);
  const [actionError, setActionError] = useState(null);
  const [starting, setStarting] = useState(false);
  const bodyRef = useRef(null);
  const commentsRef = useRef(null);
  const saveTimer = useRef(null);
  const sentBucket = useRef(0);

  useEffect(() => {
    setArticle(null);
    setError(null);
    articleService.read(slug)
      .then(a => {
        setArticle(a);
        setLiked(a.liked);
        setLikes(a.likes);
        setSaved(a.saved);
        setRead(Boolean(a.readAt));
        setShares(a.shares);
        setComments(a.comments);
        window.scrollTo(0, 0);
      })
      .catch(err => setError(err?.response?.status === 404 ? 'notfound' : 'error'));
  }, [slug]);

  // Vindo de uma notificação de comentário (#comentarios): desce até os comentários
  const { hash } = useLocation();
  useEffect(() => {
    if (!article || hash !== '#comentarios') return undefined;
    const t = setTimeout(() => commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [article, hash]);

  const headings = useMemo(() => headingsOf(article?.bodyMd), [article?.bodyMd]);
  const isDraft = article?.status === 'draft';

  // Progresso salvo no servidor a cada 10 pontos lidos, sem pressa
  const onProgress = useCallback((pct) => {
    const bucket = Math.floor(pct / 10);
    if (!article || isDraft || bucket <= sentBucket.current) return;
    sentBucket.current = bucket;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => articleService.progress(article.id, pct).catch(() => {}), 800);
  }, [article, isDraft]);
  const { pct, active } = useReadingProgress(bodyRef, headings, onProgress);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const onTotal = useCallback((n) => setComments(n), []);

  const toggleLike = async () => {
    setLiked(!liked);
    setLikes(n => n + (liked ? -1 : 1));
    try {
      const r = await articleService.toggleLike(article.id);
      setLiked(r.liked);
      setLikes(r.likes);
    } catch (err) {
      setLiked(liked);
      setLikes(article.likes);
      setActionError(apiErrorMessage(err, 'Não foi possível curtir.'));
    }
  };

  const toggleSave = async () => {
    setSaved(!saved);
    try {
      setSaved((await articleService.toggleSave(article.id)).saved);
    } catch {
      setSaved(saved);
    }
  };

  const markRead = async () => {
    const next = !read;
    setRead(next);
    try {
      const r = await articleService.progress(article.id, next ? 100 : pct, next);
      setRead(r.read);
    } catch {
      setRead(!next);
    }
  };

  const onShare = async (channel) => {
    try {
      setShares((await articleService.share(article.id, channel)).shares);
    } catch {
      // A contagem é só informativa
    }
  };

  const miniExam = async () => {
    const p = article.practice;
    setStarting(true);
    try {
      const total = Math.min(p.questions, 20);
      const exam = await examService.createExam({
        title: `Mini-simulado · ${p.topicName}`,
        correctionMode: 'end',
        totalQuestions: total,
        durationMinutes: Math.max(10, total * 2),
        categoryIds: p.categoryIds,
        examStyle: 'mixed',
        courseSlug: p.courseSlug,
      });
      navigate(`/curso/${p.courseSlug}/exam/${exam.id}`);
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Não foi possível criar o mini-simulado.'));
      setStarting(false);
    }
  };

  if (error) {
    return (
      <GlobalShell>
        <EmptyState
          file="artigo.xml"
          title={error === 'notfound' ? 'Artigo não encontrado' : 'Não foi possível abrir o artigo'}
          description={error === 'notfound' ? 'Ele pode ter sido removido ou ainda ser um rascunho.' : 'Verifique sua conexão e tente de novo.'}
          action={<Button onClick={() => navigate('/artigos')}>Ver artigos</Button>}
        />
      </GlobalShell>
    );
  }
  if (!article) return <GlobalShell><Loader label="Abrindo artigo..." /></GlobalShell>;

  const firstTag = article.tags[0];
  const demo = Boolean(user?.demo);
  const likeLocked = demo || isDraft;
  const likeTitle = demo ? 'Curtidas ficam desligadas na demonstração' : liked ? 'Descurtir' : 'Curtir';
  const likeText = likes === 0
    ? 'Seja a primeira pessoa a curtir'
    : liked
      ? (likes === 1 ? 'Você curtiu' : `Você e mais ${likes - 1} curtiram`)
      : `${likes} ${likes === 1 ? 'pessoa curtiu' : 'pessoas curtiram'}`;

  return (
    <GlobalShell>
      <div className="rd-page">
        <Breadcrumbs items={[
          { label: 'Artigos', onClick: () => navigate('/artigos') },
          ...(firstTag ? [{ label: firstTag.name, onClick: () => navigate(`/artigos/tema/${firstTag.slug}`) }] : []),
          { label: article.title },
        ]} />

        {isDraft && (
          <div className="rd-draft" role="status">
            Rascunho: só você vê este artigo.
            <Button size="sm" icon={Pencil} onClick={() => navigate(`/artigos/${article.id}/editar`)}>Continuar editando</Button>
          </div>
        )}

        <ReaderHeader
          article={article}
          showVisibility
          actions={(
            <>
              <ActionButton
                icon={Heart}
                className={`rd-likebtn ${liked ? 'is-liked' : ''}`}
                onClick={toggleLike}
                disabled={likeLocked}
                aria-pressed={liked}
                aria-label={`${liked ? 'Descurtir' : 'Curtir'} (${likes})`}
                title={likeTitle}
              >
                {likes}
              </ActionButton>
              <ActionButton
                icon={saved ? BookmarkCheck : Bookmark}
                className={saved ? 'is-saved' : ''}
                onClick={toggleSave}
                disabled={isDraft}
                aria-pressed={saved}
              >
                {saved ? 'Salvo' : 'Salvar'}
              </ActionButton>
              {!isDraft && <SharePopover article={article} onShare={onShare} />}
              {article.canEdit && (
                <>
                  <span className="rd-head__sep" aria-hidden="true" />
                  <button type="button" className="rd-editlink" onClick={() => navigate(`/artigos/${article.id}/editar`)} data-tour="artigo-editar">
                    <Pencil size={14} aria-hidden="true" />Editar
                  </button>
                </>
              )}
            </>
          )}
        />
        {actionError && <p className="rd-error" role="alert">{actionError}</p>}

        <div className="rd-grid">
          <div className="rd-col">
            {article.coverUrl && <img className="rd-cover" src={article.coverUrl} alt="" onError={(e) => { e.currentTarget.hidden = true; }} />}
            <ArticleBody className="rd-text" markdown={article.bodyMd} bodyRef={bodyRef} />

            <div className="rd-end">
              {article.tags.length > 0 && (
                <div className="rd-tags">
                  {article.tags.map(t => (
                    <button key={t.slug} type="button" onClick={() => navigate(`/artigos/tema/${t.slug}`)}># {t.name}</button>
                  ))}
                </div>
              )}
              {!isDraft && (
                <div className={`rd-endbar ${read ? 'is-read' : ''}`}>
                  <ActionButton
                    icon={Heart}
                    className={`rd-likebtn ${liked ? 'is-liked' : ''}`}
                    onClick={toggleLike}
                    disabled={likeLocked}
                    aria-pressed={liked}
                    title={likeTitle}
                  >
                    {liked ? 'Curtido' : 'Curtir'}
                  </ActionButton>
                  <span className={`rd-endbar__who ${liked ? 'is-liked' : ''}`}>{likeText}</span>
                  <SharePopover article={article} onShare={onShare} />
                  <span className="rd-endbar__read">
                    <span>{read ? 'Leitura concluída' : 'Terminou de ler?'}</span>
                    <Button variant={read ? 'outline' : 'accent'} size="sm" icon={read ? undefined : Check} onClick={markRead}>
                      {read ? 'Desmarcar' : 'Marcar como lido'}
                    </Button>
                  </span>
                </div>
              )}
            </div>

            {(article.neighbors?.prev || article.neighbors?.next) && (
              <PagerNav
                prev={article.neighbors.prev && {
                  hint: 'Artigo anterior', label: article.neighbors.prev.title, onClick: () => navigate(`/artigos/${article.neighbors.prev.slug}`),
                }}
                next={article.neighbors.next && {
                  hint: firstTag ? `Próximo em #${firstTag.name}` : 'Próximo', label: article.neighbors.next.title,
                  onClick: () => navigate(`/artigos/${article.neighbors.next.slug}`),
                }}
              />
            )}

            {!isDraft && <Comments article={article} user={user} onTotal={onTotal} anchorRef={commentsRef} />}
          </div>

          <aside className="rd-side">
            <TocCard
              headings={headings}
              pct={pct}
              active={active}
              done={read}
              footer={!isDraft && <EngagementLine likes={likes} comments={comments} shares={shares} liked={liked} />}
            />
            <LinksCard
              links={article.links}
              onOpen={(l) => navigate(l.kind === 'topic'
                ? `/curso/${l.courseSlug}/estudo/${l.topicId}`
                : `/curso/${l.courseSlug}/estudo/${l.topicId}/aula/${l.lessonId}`)}
            />
            {article.practice && (
              <section className="rd-practice">
                <span className="rd-practice__eyebrow">Pratique o tema</span>
                {article.practice.questions > 0 && (
                  <div className="rd-practice__stat">
                    <span><FileQuestion size={18} aria-hidden="true" /></span>
                    <div><strong>{article.practice.questions}</strong><span>questões vinculadas</span></div>
                  </div>
                )}
                {article.practice.flashcards > 0 && (
                  <div className="rd-practice__stat">
                    <span><Layers size={18} aria-hidden="true" /></span>
                    <div><strong>{article.practice.flashcards}</strong><span>flashcards</span></div>
                  </div>
                )}
                <div className="rd-practice__actions">
                  {article.practice.questions > 0 && (
                    <Button variant="accent" icon={Target} onClick={miniExam} disabled={starting}>
                      Mini-simulado ({Math.min(article.practice.questions, 20)} questões)
                    </Button>
                  )}
                  {article.practice.flashcards > 0 && (
                    <button type="button" className="rd-practice__ghost" onClick={() => navigate(`/curso/${article.practice.courseSlug}/flashcards`)}>
                      <Layers size={17} aria-hidden="true" />Revisar flashcards
                    </button>
                  )}
                </div>
              </section>
            )}
          </aside>
        </div>
      </div>
    </GlobalShell>
  );
}

export default ArticleView;
